/**
 * Centralizes Shipping eligibility, order-item, and presentation decisions.
 * Inputs come from merchant entitlements, cart and order data, the rollout
 * flag, and item measurements; outputs drive Shipping validation and UI.
 */

import type { ItemDimensions, Measurement } from "@/dto/serviceItem";
import type { PerBoxFee, ShippingPreferences } from "@/dto/merchant";
import { OrderType, ServiceType } from "@/constants/enum";
import { SHIPPING_LINE_ITEM_TYPE } from "@/utils/constants";

const SHIPPING_MODULE = "shipment";

type ShippingMerchant = {
  modules?: {
    elements?: Array<{ name: string }> | null;
  } | null;
  services?: Array<{ type: string; enabled?: boolean }> | null;
};

const hasEnabledService = (
  merchant: ShippingMerchant,
  serviceTypes: ServiceType[]
): boolean =>
  Boolean(
    merchant.services?.some(
      (service) =>
        serviceTypes.includes(service.type as ServiceType) &&
        service.enabled === true
    )
  );

interface ShippingSpecificationsInput {
  merchant: ShippingMerchant;
  shippingEnabled: boolean;
  dimensions: string | null;
  weight: string | null;
}

type MeasurementTranslator = (
  key: string,
  values?: Record<string, string | number>
) => string;

export const ShippingCartBannerState = {
  UNSHIPPABLE: "unshippable",
  MINIMUM_NOT_MET: "minimumNotMet",
  FREE_SHIPPING_UNLOCKED: "freeShippingUnlocked",
} as const;

export type ShippingCartBannerState =
  | (typeof ShippingCartBannerState)[keyof typeof ShippingCartBannerState]
  | null;

interface ShippingCartValidationInput {
  subtotal: number;
  hasUnshippableItems: boolean;
  shippingPreferences?: ShippingPreferences | null;
}

interface FreeShippingMethodInput {
  methods: PerBoxFee[];
  subtotalInCents: number;
  freeShipping?: boolean;
  freeShippingThreshold?: number | null;
}

interface FreeShippingRemainingInput {
  subtotalInCents: number;
  shippingPreferences?: ShippingPreferences | null;
}

interface ShippingCartItemLimitInput {
  orderType: OrderType;
  cartItems: Array<{ value?: { quantity?: number } }>;
  requestedQuantity: number;
  maxItems?: number | null;
}

interface ShippingCartCurrentItemLimitInput {
  cartItems: Array<{ value?: { quantity?: number } }>;
  maxItems?: number | null;
}

interface ShippingCartItemAvailabilityInput {
  value?: {
    isShippable?: boolean;
    isAgeRestricted?: boolean;
  };
}

export interface ShippingFeeLineItemInput {
  id?: string;
  name?: string;
  availableForOrdering?: boolean;
  lineItemType?: string;
}

export interface ShippingCartValidation {
  bannerState: ShippingCartBannerState;
  checkoutBlocked: boolean;
  minimumAmount: number | null;
}

const isValidThreshold = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const isValidMaxItems = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

const getCartItemQuantity = (
  cartItems: Array<{ value?: { quantity?: number } }>
) =>
  cartItems.reduce((total, item) => {
    const itemQuantity = item.value?.quantity;
    return total +
      (typeof itemQuantity === "number" && Number.isFinite(itemQuantity)
        ? Math.max(itemQuantity, 0)
        : 0);
  }, 0);

/** Returns cents left until free shipping, or null when no checkout reminder is needed. */
export const getRemainingForFreeShippingCents = ({
  subtotalInCents,
  shippingPreferences,
}: FreeShippingRemainingInput): number | null => {
  const threshold = shippingPreferences?.freeShippingThreshold;
  if (
    shippingPreferences?.freeShipping !== true ||
    !isValidThreshold(threshold) ||
    !Number.isFinite(subtotalInCents)
  ) {
    return null;
  }

  const remainingCents = Math.round(threshold * 100) - subtotalInCents;
  return remainingCents > 0 ? remainingCents : null;
};

/** Returns the cheapest eligible method when the merchandise subtotal qualifies. */
export const getFreeShippingMethodToken = ({
  methods,
  subtotalInCents,
  freeShipping,
  freeShippingThreshold,
}: FreeShippingMethodInput): string | null => {
  if (
    freeShipping !== true ||
    !isValidThreshold(freeShippingThreshold) ||
    !Number.isFinite(subtotalInCents) ||
    subtotalInCents < Math.round(freeShippingThreshold * 100)
  ) {
    return null;
  }

  const eligibleMethods = methods.filter(
    (method) => Number.isFinite(method.fee) && method.fee >= 0
  );
  const cheapestMethod = [...eligibleMethods].sort(
    (first, second) => first.fee - second.fee || first.ordinal - second.ordinal
  )[0];

  return cheapestMethod?.serviceLevelToken ?? null;
};

/** Returns true when adding the requested units would exceed Shipping capacity. */
export const wouldExceedShippingCartItemLimit = ({
  orderType,
  cartItems,
  requestedQuantity,
  maxItems,
}: ShippingCartItemLimitInput): boolean => {
  if (
    orderType !== OrderType.SHIPPING ||
    !isValidMaxItems(maxItems) ||
    !Number.isInteger(requestedQuantity) ||
    requestedQuantity <= 0
  ) {
    return false;
  }

  return getCartItemQuantity(cartItems) + requestedQuantity > maxItems;
};

/** Returns true when the current cart already exceeds Shipping capacity. */
export const isShippingCartItemLimitExceeded = ({
  cartItems,
  maxItems,
}: ShippingCartCurrentItemLimitInput): boolean =>
  isValidMaxItems(maxItems) && getCartItemQuantity(cartItems) > maxItems;

/** Treats missing Shipping metadata and age-restricted merchandise as unavailable. */
export const isItemUnavailableForShipping = (
  item: ShippingCartItemAvailabilityInput
): boolean =>
  item.value?.isShippable !== true || item.value?.isAgeRestricted === true;

/** Identifies explicit and legacy Shipping fee rows without hiding real products. */
export const isShippingFeeLineItem = (
  item: ShippingFeeLineItemInput,
  orderType?: OrderType
): boolean =>
  item.lineItemType === SHIPPING_LINE_ITEM_TYPE ||
  (orderType === OrderType.SHIPPING &&
    item.id === "" &&
    item.name === "Shipping" &&
    item.availableForOrdering === false);

/** Derives the highest-priority Shipping banner and checkout eligibility. */
export const getShippingCartValidation = ({
  subtotal,
  hasUnshippableItems,
  shippingPreferences,
}: ShippingCartValidationInput): ShippingCartValidation => {
  if (hasUnshippableItems) {
    return {
      bannerState: ShippingCartBannerState.UNSHIPPABLE,
      checkoutBlocked: true,
      minimumAmount: null,
    };
  }

  const orderMinimum = shippingPreferences?.shippingOrderMinimum;
  if (isValidThreshold(orderMinimum) && subtotal < orderMinimum) {
    return {
      bannerState: ShippingCartBannerState.MINIMUM_NOT_MET,
      checkoutBlocked: true,
      minimumAmount: orderMinimum,
    };
  }

  const freeShippingThreshold = shippingPreferences?.freeShippingThreshold;
  if (
    shippingPreferences?.freeShipping === true &&
    isValidThreshold(freeShippingThreshold) &&
    subtotal >= freeShippingThreshold
  ) {
    return {
      bannerState: ShippingCartBannerState.FREE_SHIPPING_UNLOCKED,
      checkoutBlocked: false,
      minimumAmount: null,
    };
  }

  return {
    bannerState: null,
    checkoutBlocked: false,
    minimumAmount: null,
  };
};

/** Hides banners rendered elsewhere and suppresses free Shipping behind item limits. */
export const getVisibleShippingCartBannerState = (
  bannerState: ShippingCartBannerState,
  shippingItemLimitExceeded: boolean
) => {
  if (
    bannerState === null ||
    bannerState === ShippingCartBannerState.UNSHIPPABLE
  ) {
    return null;
  }

  if (
    shippingItemLimitExceeded &&
    bannerState === ShippingCartBannerState.FREE_SHIPPING_UNLOCKED
  ) {
    return null;
  }

  return bannerState;
};

/** Returns true when Shipping rollout and merchant entitlement are both active. */
export const isShippingAvailable = (
  merchant: ShippingMerchant,
  shippingEnabled: boolean,
): boolean =>
  Boolean(
    shippingEnabled &&
    merchant.services?.some(
      (service) =>
        service.type === ServiceType.SHIPPING && service.enabled === true
    ) &&
    merchant.modules?.elements?.some(
      (element) => element.name === SHIPPING_MODULE
    )
  );

/** Resolves a valid initial fulfillment type for a retail checkout. */
export const resolveInitialCheckoutOrderType = (
  merchant: ShippingMerchant,
  savedOrderType: OrderType | null | undefined,
  shippingAvailable: boolean
): OrderType | null => {
  const pickupEnabled = hasEnabledService(merchant, [
    ServiceType.PICKUP,
    ServiceType.WEBSITE,
  ]);
  const deliveryEnabled = hasEnabledService(merchant, [ServiceType.DELIVERY]);

  if (savedOrderType === OrderType.PICKUP && pickupEnabled) {
    return OrderType.PICKUP;
  }
  if (savedOrderType === OrderType.DELIVERY && deliveryEnabled) {
    return OrderType.DELIVERY;
  }
  if (savedOrderType === OrderType.SHIPPING && shippingAvailable) {
    return OrderType.SHIPPING;
  }

  if (pickupEnabled) return OrderType.PICKUP;
  if (deliveryEnabled) return OrderType.DELIVERY;
  if (shippingAvailable) return OrderType.SHIPPING;

  return null;
};

/** Chooses a usable legacy fulfillment when stale Shipping state is rejected. */
export const getShippingFallbackOrderType = (
  merchant: ShippingMerchant
): OrderType | null =>
  resolveInitialCheckoutOrderType(merchant, null, false);


const formatMeasurementValue = (
  measurement: Measurement | undefined,
  translationKey: string,
  translate: MeasurementTranslator
): string | null => {
  if (
    !measurement ||
    !Number.isFinite(measurement.value) ||
    measurement.value < 0
  ) {
    return null;
  }

  return translate(translationKey, { value: measurement.value });
};

const getDimensionMeasurements = (dimensions?: ItemDimensions) => [
  { axis: "length", measurement: dimensions?.length },
  { axis: "width", measurement: dimensions?.width },
  { axis: "height", measurement: dimensions?.height },
];

/** Formats one valid dimension value using the API's imperial display contract. */
export const formatDimensionValue = (
  measurement: Measurement | undefined,
  translate: MeasurementTranslator
): string | null =>
  formatMeasurementValue(measurement, "dimensionInches", translate);

const formatDimensionList = (
  values: string[],
  translate: MeasurementTranslator,
  twoValueKey = "dimensionsTwo",
  threeValueKey = "dimensionsThree"
): string | null => {
  if (values.length === 3) {
    return translate(threeValueKey, {
      first: values[0],
      second: values[1],
      third: values[2],
    });
  }
  if (values.length === 2) {
    return translate(twoValueKey, {
      first: values[0],
      second: values[1],
    });
  }

  return values[0] ?? null;
};

/** Formats the available dimensions in canonical length, width, height order. */
export const formatDimensions = (
  dimensions: ItemDimensions | undefined,
  translate: MeasurementTranslator
): string | null => {
  const values = getDimensionMeasurements(dimensions)
    .map(({ axis, measurement }) => {
      const value = formatMeasurementValue(
        measurement,
        "dimensionInchesCompact",
        translate
      );
      return value
        ? translate("dimensionAxisValueCompact", {
            axis: translate(`${axis}AxisShort`),
            value,
          })
        : null;
    })
    .filter((value): value is string => value !== null);

  return formatDimensionList(
    values,
    translate,
    "dimensionsCompactTwo",
    "dimensionsCompactThree"
  );
};

/** Identifies each available dimension axis for a screen-reader label. */
export const formatDimensionsAriaLabel = (
  dimensions: ItemDimensions | undefined,
  translate: MeasurementTranslator
): string | null => {
  const values = getDimensionMeasurements(dimensions)
    .map(({ axis, measurement }) => {
      const value = formatDimensionValue(measurement, translate);
      return value
        ? translate("dimensionAxisValue", {
          axis: translate(`${axis}Axis`),
          value,
        })
        : null;
    })
    .filter((value): value is string => value !== null);

  return formatDimensionList(values, translate);
};

/** Formats a valid item weight using the API's imperial display contract. */
export const formatWeight = (
  dimensions: ItemDimensions | undefined,
  translate: MeasurementTranslator
): string | null => {
  return formatMeasurementValue(dimensions?.weight, "weightPounds", translate);
};

/** Returns true only when merchant and item Shipping requirements are met. */
export const shouldShowShippingSpecifications = ({
  merchant,
  shippingEnabled,
  dimensions,
  weight,
}: ShippingSpecificationsInput): boolean => {
  const hasShipmentModule = merchant.modules?.elements?.some(
    (element) => element.name === SHIPPING_MODULE
  );

  return Boolean(
    shippingEnabled &&
    hasShipmentModule &&
    (dimensions || weight)
  );
};
